// Individual Meal Plan 2.0 detail page. Shows plan summary, sample
// weekly menu (first upcoming week for a representative area) and a
// direct configure CTA. Public route — content varies by MP2 mode.
import { createFileRoute, notFound } from "@tanstack/react-router";
import { useState } from "react";
import { SiteLayout } from "@/components/SiteLayout";
import { PageHero } from "@/components/PageHero";
import { Section } from "@/components/Section";
import { usePublicMp2Catalog } from "@/hooks/usePublicMp2Catalog";
import { MealPlanProductPage } from "@/components/mp2/MealPlanProductPage";
import { getPublicMp2Catalog } from "@/lib/public/meal-plans-v2.functions";

/** Readable plan name from its slug (e.g. "weight-management" → "Weight Management"). */
function planNameFromSlug(slug: string) {
  return slug.split("-").filter(Boolean).map((w) => w[0].toUpperCase() + w.slice(1)).join(" ");
}

export const Route = createFileRoute("/meal-plans_/$slug")({
  head: ({ params, loaderData }) => {
    if (!loaderData) {
      return { meta: [{ title: "Meal plan not found — Poonam Kapur Healthy Kitchen" }, { name: "robots", content: "noindex" }] };
    }
    const name = planNameFromSlug(params.slug);
    const url = `https://poonamkapur.com/meal-plans/${params.slug}`;
    const title = `${name} Meal Plan — Poonam Kapur Healthy Kitchen`;
    return {
      meta: [
        { title },
        {
          name: "description",
          content: `Sample weekly menu, dish variety and pricing for the ${name} meal plan, delivered across Mumbai.`,
        },
        { property: "og:title", content: title },
        { property: "og:description", content: `See the sample weekly menu and configure the ${name} meal plan.` },
        { property: "og:url", content: url },
        { property: "og:type", content: "website" },
        { name: "twitter:card", content: "summary_large_image" },
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Home", item: "https://poonamkapur.com/" },
              { "@type": "ListItem", position: 2, name: "Meal Plans", item: "https://poonamkapur.com/meal-plans" },
              { "@type": "ListItem", position: 3, name: `${name} Meal Plan`, item: url },
            ],
          }),
        },
      ],
    };
  },
  // Server-side existence check so unknown plan slugs return a real 404.
  // If the catalog can't be read, render normally and let the page handle it.
  loader: async ({ params }) => {
    const catalog = await getPublicMp2Catalog();
    if (catalog.unavailable) return { ok: true };
    const exists = (catalog.plans ?? []).some((p) => p.slug === params.slug);
    if (!exists && !catalog.is_admin) throw notFound();
    return { ok: true };
  },
  component: PlanDetail,
  notFoundComponent: () => (
    <SiteLayout>
      <PageHero eyebrow="Meal Plans" title="Plan not found" intro="This meal plan is not available." />
    </SiteLayout>
  ),
  errorComponent: ({ error }) => (
    <SiteLayout>
      <PageHero eyebrow="Meal Plans" title="We couldn't load this plan" intro={(error as Error | undefined)?.message ?? "Please try again."} />
    </SiteLayout>
  ),
});

function PlanDetail() {
  const { slug } = Route.useParams();
  const { catalog, isMp2Visible, isLoading } = usePublicMp2Catalog();
  const [selectedDishIds, setSelectedDishIds] = useState<string[]>([]);

  if (isLoading) {
    return (
      <SiteLayout>
        <PageHero eyebrow="Meal plan" title={`${planNameFromSlug(slug)} Meal Plan`} />
        <Section><div className="text-muted-foreground">Loading plan…</div></Section>
      </SiteLayout>
    );
  }
  if (!isMp2Visible || !catalog) {
    throw notFound();
  }
  const plan = catalog.plans.find((p) => p.slug === slug);
  if (!plan) throw notFound();

  return (
    <SiteLayout>
      <PageHero
        eyebrow={plan.health_review_required ? "Health-focused plan" : "Meal plan"}
        title={plan.title}
        intro={plan.tagline ?? plan.description ?? undefined}
      />
      <Section>
        <MealPlanProductPage plan={plan} preferredDishIds={selectedDishIds} />
      </Section>
    </SiteLayout>
  );
}
